import { FC, ReactNode, useEffect, useState } from "react";
import { FormattedMessage, IntlShape, useIntl } from "react-intl";

import { KeyboardArrowDown, KeyboardArrowRight } from "@mui/icons-material";
import { Box, Collapse, IconButton, Stack, Typography } from "@mui/material";

import { IShortTermPredictItem } from "../scheme/components/state-and-forecast/interfaces";
import {
  formatShortTermPredictText,
  getShortTermPredictBuildDate,
  getShortTermPredictValidUntilDate,
} from "../scheme/components/state-and-forecast/utils";
import { useAlarmPaletteResolver } from "../../../../hooks/use-alarm-color";
import { ALARMS, formatDateHumanReadable, SemaforIcon } from "../../../../utils";
import { IRulPredictItem } from "./interfaces";
import { buildRulTimeline, formatRulPredictPercent, hasRulPredictData, IRulTimelineModel } from "./utils";

const ForecastResultCard: FC<{ children: ReactNode }> = ({ children }) => (
  <Box
    sx={{
      px: 1,
      py: 0.75,
      borderRadius: 1,
      backgroundColor: "rgba(54, 56, 70, 0.06)",
    }}
  >
    {children}
  </Box>
);

const ShortTermForecastSection: FC<{
  isLoading?: boolean;
  items: IShortTermPredictItem[];
}> = ({ isLoading = false, items }) => {
  const { formatMessage } = useIntl();

  return (
    <Box sx={{ mb: 1.5 }}>
      <Typography
        sx={{
          fontWeight: 600,
          fontSize: { lg: 15, md: 13 },
          lineHeight: { lg: "20px", md: "18px" },
          color: "#363846",
          mb: 1,
        }}
      >
        <FormattedMessage id="pages.scheme.stateAndForecast.shortTermForecastTitle" />
      </Typography>
      {isLoading ? (
        <Typography sx={{ fontSize: 12, lineHeight: "16px", color: "#5f6472" }}>
          <FormattedMessage id="pages.forecasting.shortTermForecastLoading" />
        </Typography>
      ) : items.length === 0 ? (
        <Typography sx={{ fontSize: 12, lineHeight: "16px", color: "#5f6472" }}>
          <FormattedMessage id="pages.forecasting.shortTermForecastEmpty" />
        </Typography>
      ) : (
        <Stack spacing={1.25}>
          {items.map(item => {
            const buildDate = getShortTermPredictBuildDate(item);
            const validUntilDate = getShortTermPredictValidUntilDate(item);
            const hasValidDate = Number.isFinite(new Date(buildDate).getTime());
            const hasValidUntilDate = validUntilDate !== null && Number.isFinite(new Date(validUntilDate).getTime());
            const asOfLine = hasValidDate
              ? formatMessage(
                  { id: "pages.scheme.stateAndForecast.shortTermForecastAsOf" },
                  { date: formatDateHumanReadable(buildDate) },
                )
              : null;
            const validUntilLine =
              hasValidUntilDate && validUntilDate
                ? formatMessage(
                    { id: "pages.scheme.stateAndForecast.shortTermForecastValidUntil" },
                    { date: formatDateHumanReadable(validUntilDate) },
                  )
                : null;

            return (
              <ForecastResultCard key={`${item.idTrainMechanism}-${buildDate}`}>
                <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#363846", mb: 0.25 }}>
                  {item.mechanismName ||
                    formatMessage({ id: "pages.scheme.stateAndForecast.anomalyTooltip.sourceTypes.mechanism" })}
                </Typography>
                {asOfLine ? (
                  <Typography
                    sx={{ fontSize: 11, lineHeight: "14px", color: "#5f6472", mb: validUntilLine ? 0.25 : 0.5 }}
                  >
                    {asOfLine}
                  </Typography>
                ) : null}
                {validUntilLine ? (
                  <Typography sx={{ fontSize: 11, lineHeight: "14px", color: "#5f6472", mb: 0.5 }}>
                    {validUntilLine}
                  </Typography>
                ) : null}
                <Typography sx={{ fontSize: 12, lineHeight: "16px", color: "#363846", whiteSpace: "pre-wrap" }}>
                  {formatShortTermPredictText(item.mainResults)}
                </Typography>
                {item.additionalInfo ? (
                  <Typography
                    sx={{ fontSize: 11, lineHeight: "15px", color: "#5f6472", mt: 0.5, whiteSpace: "pre-wrap" }}
                  >
                    {formatShortTermPredictText(item.additionalInfo)}
                  </Typography>
                ) : null}
              </ForecastResultCard>
            );
          })}
        </Stack>
      )}
    </Box>
  );
};

const RUL_RISK_LABEL_IDS: Record<IRulTimelineModel["markers"][number]["horizonDays"], string> = {
  7: "pages.forecasting.rulRisk7d",
  30: "pages.forecasting.rulRisk30d",
  60: "pages.forecasting.rulRisk60d",
  90: "pages.forecasting.rulRisk90d",
};

const RulTimeline: FC<{
  color: string;
  formatMessage: IntlShape["formatMessage"];
  formatNumber: IntlShape["formatNumber"];
  model: IRulTimelineModel;
  scaleDays: number;
}> = ({ color, formatMessage, formatNumber, model, scaleDays }) => {
  const lineWidth = (model.remainingDays / scaleDays) * 100;
  const hasMarkerNearStart = model.markers.some(marker => (marker.daysFromIntervalEnd / scaleDays) * 100 < 12);

  return (
    <Box sx={{ mt: 2, mb: 0.25 }}>
      <Typography sx={{ fontSize: 20, lineHeight: "30px", color: "#363846", mb: 0.5 }}>
        {formatMessage({ id: "pages.forecasting.rulDays" }, { value: Math.round(model.remainingDays * 10) / 10 })}
      </Typography>
      <Box sx={{ position: "relative", height: 70 }}>
        <Box
          aria-label={formatMessage(
            { id: "pages.forecasting.rulDays" },
            { value: Math.round(model.remainingDays * 10) / 10 },
          )}
          sx={{
            position: "absolute",
            left: 0,
            width: `${lineWidth}%`,
            top: 24,
            height: 12,
            backgroundColor: color,
            border: color.toLowerCase() === "#ffffff" ? "1px solid #aeb4c1" : "none",
          }}
        />
        {hasMarkerNearStart ? null : (
          <Typography
            sx={{ position: "absolute", left: 0, top: 0, fontSize: 13, lineHeight: "16px", color: "#5f6472" }}
          >
            0 {formatMessage({ id: "pages.forecasting.rulDayShort" })}
          </Typography>
        )}
        {model.markers.map(marker => {
          const label = formatMessage({ id: RUL_RISK_LABEL_IDS[marker.horizonDays] });
          const probability = formatRulPredictPercent(marker.value, formatNumber);
          const markerOffset = (marker.daysFromIntervalEnd / scaleDays) * 100;
          const nearStart = markerOffset < 8;
          const labelTransform = nearStart ? "translateX(0)" : "translateX(-50%)";

          return (
            <Box
              key={marker.riskKey}
              title={`${label}: ${probability}`}
              sx={{
                position: "absolute",
                left: `${markerOffset}%`,
                top: 0,
                width: 0,
              }}
            >
              <Typography
                sx={{
                  position: "absolute",
                  left: 0,
                  top: 0,
                  transform: labelTransform,
                  fontSize: 13,
                  lineHeight: "16px",
                  color: "#5f6472",
                  whiteSpace: "nowrap",
                }}
              >
                {formatNumber(marker.daysFromIntervalEnd, { maximumFractionDigits: 1 })}{" "}
                {formatMessage({ id: "pages.forecasting.rulDayShort" })}
              </Typography>
              <Box
                sx={{
                  position: "absolute",
                  left: 0,
                  top: 18,
                  width: 2,
                  height: 28,
                  ml: "-1px",
                  backgroundColor: "#11131a",
                }}
              />
              <Typography
                sx={{
                  position: "absolute",
                  left: 0,
                  top: 48,
                  transform: labelTransform,
                  fontSize: 13,
                  lineHeight: "16px",
                  color: "#11131a",
                  whiteSpace: "nowrap",
                }}
              >
                {probability}
              </Typography>
            </Box>
          );
        })}
      </Box>
      {model.markers.length > 0 ? (
        <Typography
          sx={{
            mt: 0.75,
            width: `${lineWidth}%`,
            minWidth: 140,
            textAlign: "center",
            fontSize: 11,
            lineHeight: "14px",
            color: "#363846",
          }}
        >
          <FormattedMessage id="pages.forecasting.rulRiskTitle" />
        </Typography>
      ) : null}
    </Box>
  );
};

const RulForecastSection: FC<{
  formatDate: (date: Date | string) => string;
  formatNumber: IntlShape["formatNumber"];
  intervalEndTimestamp: string | Date;
  isLoading?: boolean;
  items: IRulPredictItem[];
}> = ({ formatDate, formatNumber, intervalEndTimestamp, isLoading = false, items }) => {
  const { formatMessage } = useIntl();
  const resolveAlarmColor = useAlarmPaletteResolver();
  const visibleItems = items.filter(hasRulPredictData);
  const timelineItems = visibleItems.map(item => ({
    item,
    timeline: buildRulTimeline(item, intervalEndTimestamp),
  }));
  const scaleDays = Math.max(1, ...timelineItems.map(({ timeline }) => timeline?.remainingDays ?? 0));

  return (
    <Box sx={{ mt: 1.5 }}>
      <Typography
        sx={{
          fontWeight: 600,
          fontSize: { lg: 15, md: 13 },
          lineHeight: { lg: "20px", md: "18px" },
          color: "#363846",
          mb: 1,
        }}
      >
        <FormattedMessage id="pages.forecasting.rulForecastTitle" />
      </Typography>
      {isLoading ? (
        <Typography sx={{ fontSize: 12, lineHeight: "16px", color: "#5f6472" }}>
          <FormattedMessage id="pages.forecasting.rulForecastLoading" />
        </Typography>
      ) : visibleItems.length === 0 ? (
        <Typography sx={{ fontSize: 12, lineHeight: "16px", color: "#5f6472" }}>
          <FormattedMessage id="pages.forecasting.rulForecastEmpty" />
        </Typography>
      ) : (
        <Stack spacing={1.25}>
          {timelineItems.map(({ item, timeline }) => {
            const hasValidDate = Number.isFinite(new Date(item.date).getTime());
            const asOfLine = hasValidDate
              ? formatMessage({ id: "pages.forecasting.rulForecastAsOf" }, { date: formatDate(item.date) })
              : null;
            const statusLabel = item.alarmStatus !== null ? ALARMS[item.alarmStatus] ?? String(item.alarmStatus) : null;

            return (
              <ForecastResultCard key={`${item.idTrainMechanism}-${item.date}`}>
                <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#363846", mb: 0.25 }}>
                  {item.mechanismName ||
                    formatMessage({ id: "pages.scheme.stateAndForecast.anomalyTooltip.sourceTypes.mechanism" })}
                </Typography>
                {asOfLine ? (
                  <Typography sx={{ fontSize: 11, lineHeight: "14px", color: "#5f6472", mb: 0.5 }}>
                    {asOfLine}
                  </Typography>
                ) : null}
                {statusLabel ? (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 0.25 }}>
                    <Typography sx={{ fontSize: 12, lineHeight: "16px", color: "#363846" }}>
                      {formatMessage({ id: "pages.forecasting.rulStatus" }, { status: statusLabel })}
                    </Typography>
                    {item.alarmStatus !== null ? (
                      <SemaforIcon status={item.alarmStatus} width={14} height={14} />
                    ) : null}
                  </Box>
                ) : null}
                {timeline ? (
                  <RulTimeline
                    color={item.alarmStatus === null ? "#c8cedd" : resolveAlarmColor(item.alarmStatus)}
                    formatMessage={formatMessage}
                    formatNumber={formatNumber}
                    model={timeline}
                    scaleDays={scaleDays}
                  />
                ) : null}
              </ForecastResultCard>
            );
          })}
        </Stack>
      )}
    </Box>
  );
};

export const ForecastResultsPanel: FC<{
  formatDate: (date: Date | string) => string;
  formatNumber: IntlShape["formatNumber"];
  intervalEndTimestamp: string | Date;
  isRulLoading?: boolean;
  isShortTermLoading?: boolean;
  rulItems: IRulPredictItem[];
  shortTermItems: IShortTermPredictItem[];
}> = ({
  formatDate,
  formatNumber,
  intervalEndTimestamp,
  isRulLoading = false,
  isShortTermLoading = false,
  rulItems,
  shortTermItems,
}) => {
  const { formatMessage } = useIntl();
  const [expanded, setExpanded] = useState(false);
  const isLoading = isShortTermLoading || isRulLoading;
  const visibleRulItems = rulItems.filter(hasRulPredictData);
  const hasAnyContent = shortTermItems.length > 0 || visibleRulItems.length > 0;

  useEffect(() => {
    if (isLoading) {
      return;
    }

    setExpanded(hasAnyContent);
  }, [hasAnyContent, isLoading]);

  return (
    <Box
      sx={{
        mx: { xs: 1, md: 2 },
        mt: 1.25,
        mb: 0.75,
        border: "1px solid #d7dde9",
        borderRadius: 0.75,
        overflow: "hidden",
        backgroundColor: "#ffffff",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 1,
          py: 0.75,
          backgroundColor: "#f5f7fb",
          borderBottom: expanded ? "1px solid #d7dde9" : "none",
        }}
      >
        <Typography sx={{ fontSize: 13, lineHeight: "16px", fontWeight: 600, color: "#363846" }}>
          <FormattedMessage id="pages.forecasting.forecastResultsTitle" />
        </Typography>
        <IconButton
          size="small"
          aria-expanded={expanded}
          aria-label={formatMessage({
            id: expanded ? "pages.forecasting.collapseForecastResults" : "pages.forecasting.expandForecastResults",
          })}
          onClick={() => setExpanded(current => !current)}
        >
          {expanded ? <KeyboardArrowDown fontSize="small" /> : <KeyboardArrowRight fontSize="small" />}
        </IconButton>
      </Box>
      <Collapse in={expanded} timeout="auto" unmountOnExit={false}>
        <Box sx={{ px: { xs: 1, md: 1.5 }, py: 1.25 }}>
          <ShortTermForecastSection isLoading={isShortTermLoading} items={shortTermItems} />
          <RulForecastSection
            formatDate={formatDate}
            formatNumber={formatNumber}
            intervalEndTimestamp={intervalEndTimestamp}
            isLoading={isRulLoading}
            items={rulItems}
          />
        </Box>
      </Collapse>
    </Box>
  );
};
